import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { showMenuSectionCarousel } from 'selectors/features';
import { getLocale } from 'selectors/user';

import { CarouselContainer, CarouselButton } from 'components/MenuSectionCarousel/styles';
import { trackEvent } from 'utils/tracking';
import { GuestDirectoryMenu, GuestDirectorySection } from '../types';

const MAX_CAROUSEL_LABEL_LENGTH = 20;

const truncateCarouselLabel = (name: string): string =>
  name.length > MAX_CAROUSEL_LABEL_LENGTH ? `${name.slice(0, MAX_CAROUSEL_LABEL_LENGTH)}…` : name;

interface Props {
  menuList?: GuestDirectoryMenu[];
  sectionList?: GuestDirectorySection[];
  menuSlug?: string;
  sectionSlug?: string;
}

const GuestDirectoryMenuCarousel: React.FC<Props> = ({
  menuList = [],
  sectionList = [],
  menuSlug,
  sectionSlug,
}) => {
  const showCarousel = useSelector(showMenuSectionCarousel);
  const currentLocale = useSelector(getLocale);
  const ref = useRef<HTMLElement>();

  const setRef = useCallback((el: HTMLElement | null) => {
    ref.current = el || undefined;
  }, []);

  const showSections = sectionSlug && sectionList.length > 0;
  const carouselList = showSections ? sectionList : menuList;

  useEffect(() => {
    if (ref?.current?.scrollIntoView) {
      ref.current.scrollIntoView();
    }
  }, [menuSlug, sectionSlug]);

  if (!showCarousel || !carouselList.length) return null;

  if (showSections && !menuSlug) return null;

  const handleClick = (item: GuestDirectoryMenu | GuestDirectorySection, index: number) => {
    trackEvent(`select_carousel_${showSections ? 'section' : 'menu'}`, {
      category: 'guest_directory',
      item_name: 'name' in item ? item.name : '',
      position: index + 1,
    });
  };

  return (
    <CarouselContainer>
      {carouselList.map((item, index) => {
        const itemId = item.id;
        const itemName = showSections
          ? (item as GuestDirectorySection).translations?.[currentLocale]?.name ||
            (item as GuestDirectorySection).name ||
            ''
          : (item as GuestDirectoryMenu).translations?.[currentLocale]?.name ||
            (item as GuestDirectoryMenu).name ||
            '';

        const itemSlug = showSections
          ? (item as GuestDirectorySection).slug
          : (item as GuestDirectoryMenu).slug;
        const url = showSections
          ? `/guest-directory/menu/${menuSlug}/section/${itemSlug}`
          : `/guest-directory/menu/${itemSlug}`;

        const selected = itemSlug === (showSections ? sectionSlug : menuSlug);

        return (
          <CarouselButton
            ref={selected ? setRef : null}
            key={itemId}
            label={truncateCarouselLabel(itemName)}
            to={url}
            onClick={() => handleClick(item, index)}
            disabled={selected}
          />
        );
      })}
    </CarouselContainer>
  );
};

export default GuestDirectoryMenuCarousel;
